import { TWebsiteBuilderElement } from "./element/websiteBuilderElement";
import {
  isWebsiteBuilderMainSection,
  TWebsiteBuilderMainSection,
  TWebsiteBuilderSection,
} from "./section/websiteBuilderSection";
import { TWebsiteBuilderContainer, TWebsiteBuilderContainerWithChildren, isWebsiteBuilderContainerWithChildren } from "./container/websiteBuilderContainer";
import { CSSProperties } from "react";
import { IWebsiteBuilderGridCell } from "./gridCell/websiteBuilderGridCell";
import { IWebsiteBuilderFlex } from "./container/websiteBuilderFlex";
import { IWebsiteBuilderBox } from "./container/websiteBuilderBox";
import { isWebsiteBuilderFreeformParentNode, TWebsiteBuilderFreeformParentNode } from "./websiteBuilderFreeformParentNode";
import { isWebsiteBuilderFlowParentNode, TWebsiteBuilderFlowParentNode } from "./websiteBuilderFlowParentNode";
import { IWebsiteBuilderTab } from "./tab/websiteBuilderTab";
import { IWebsiteBuilderRoot } from "./root/websiteBuilderRoot";

export interface IWebsiteBuilderNodeBase {
  id: string;
  type: string;
}

export type TWebsiteBuilderNode =
  | IWebsiteBuilderRoot
  | TWebsiteBuilderSection
  | TWebsiteBuilderContainer
  | IWebsiteBuilderGridCell
  | IWebsiteBuilderTab
  | TWebsiteBuilderElement;

export type TWebsiteBuilderNodeType = TWebsiteBuilderNode["type"];

export const isWebsiteBuilderNodeType = (type: any): type is TWebsiteBuilderNodeType => {
  return (
    type === "root" ||
    type === "section" ||
    type === "container" ||
    type === "grid-cell" ||
    type === "tab" ||
    type === "element"
  );
};

export type TWebsiteBuilderNodeDatasetModelPrefixKeys<T, Prefix extends string> = {
  [K in keyof T as `${Prefix}${Capitalize<string & K>}`]: T[K];
};

export type TWebsiteBuilderNodeDatasetFromModel<T> = { [K in keyof T]: string | undefined };

type TWebsiteBuilderParentNode =
  | IWebsiteBuilderRoot
  | TWebsiteBuilderSection
  | TWebsiteBuilderContainerWithChildren
  | IWebsiteBuilderGridCell
  | IWebsiteBuilderTab;

export const isWebsiteBuilderParentNode = (node: TWebsiteBuilderNode): node is TWebsiteBuilderParentNode => {
  return (
    node?.type === "root" ||
    node?.type === "section" ||
    isWebsiteBuilderContainerWithChildren(node) ||
    node?.type === "grid-cell" ||
    node?.type === "tab"
  );
};

export type TWebsiteBuilderElementContainerParentNode = IWebsiteBuilderBox | IWebsiteBuilderFlex;

export type TWebsiteBuilderElementParentNode =
  | TWebsiteBuilderSection
  | TWebsiteBuilderElementContainerParentNode
  | IWebsiteBuilderGridCell
  | IWebsiteBuilderTab;

export type TWebsiteBuilderElementFlowParentNode = Extract<
  TWebsiteBuilderElementParentNode,
  TWebsiteBuilderFlowParentNode
>;

export type TWebsiteBuilderElementFreeformParentNode = Extract<
  TWebsiteBuilderElementParentNode,
  TWebsiteBuilderFreeformParentNode
>;

export function isWebsiteBuilderElementContainerParentNode(node: TWebsiteBuilderNode): node is TWebsiteBuilderElementContainerParentNode {
  return node?.type === "container" && (
    node.container?.type === "box" ||
    node.container?.type === "flex"
  );
}

export const isWebsiteBuilderElementParentNode = (node: TWebsiteBuilderNode): node is TWebsiteBuilderElementParentNode => {
  return (
    node?.type === "section" ||
    isWebsiteBuilderElementContainerParentNode(node) ||
    node?.type === "grid-cell" ||
    node?.type === "tab"
  );
};

export const isWebsiteBuilderElementFlowParentNode = (node: TWebsiteBuilderNode): node is TWebsiteBuilderElementFlowParentNode => {
  return isWebsiteBuilderElementParentNode(node) && isWebsiteBuilderFlowParentNode(node);
};

export const isWebsiteBuilderElementFreeformParentNode = (node: TWebsiteBuilderNode): node is TWebsiteBuilderElementFreeformParentNode => {
  return isWebsiteBuilderElementParentNode(node) && isWebsiteBuilderFreeformParentNode(node);
};

export type TWebsiteBuilderCopyableNode =
  | TWebsiteBuilderMainSection
  | TWebsiteBuilderContainer
  | TWebsiteBuilderElement;
  
export type TWebsiteBuilderCopyableNodeType = TWebsiteBuilderCopyableNode["type"];

export const isWebsiteBuilderCopyableNode = (node: TWebsiteBuilderNode): node is TWebsiteBuilderCopyableNode => {
  return isWebsiteBuilderMainSection(node) || node?.type === "container" || node?.type === "element";
};

export type TWebsiteBuilderDraggableNode =
  | TWebsiteBuilderSection
  | TWebsiteBuilderContainer
  | TWebsiteBuilderElement;

type TWebsiteBuilderDraggableNodeType = TWebsiteBuilderDraggableNode["type"];

export type TWebsiteBuilderDraggableNodeDropItemType = `website-builder-${TWebsiteBuilderDraggableNodeType}`;

export const isWebsiteBuilderDraggableNode = (node: any): node is TWebsiteBuilderDraggableNode => {
  return node?.type === "section" || node?.type === "container" || node?.type === "element";
};

export interface IWebsiteBuilderNodeDesignStyles {
  className: string;
  styleProperties: CSSProperties;
  styleDeclaration: string;
}
